import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import ThemeModal from "components/Common/ThemeModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

import { useListParams } from "hooks/useListParams"
import { useAccountVendors, useDeleteAccountVendor } from "hooks/useAccountVendor"
import { formatReadableDate } from "utils/formatDate"
import LoanSummaryCard from "pages/AccountPages/AccountLoan/LoanSummaryCard"

import AccountVendorAddEdit from "./AccountVendorAddEdit"
import AccountVendorHeader from "./AccountVendorHeader"

const AccountVendorList = () => {
  document.title = "Account Vendor List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [loansModal, setLoansModal] = useState(false)
  const [selectedVendorForLoans, setSelectedVendorForLoans] = useState(null)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useAccountVendors(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteAccountVendor()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleViewLoansClick = item => {
    setSelectedVendorForLoans(item)
    setLoansModal(true)
  }

  const toggleLoansModal = () => {
    setLoansModal(prev => !prev)
    if (loansModal) setSelectedVendorForLoans(null)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Vendor deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      toast.error("Failed to delete vendor.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Phone",
        accessorKey: "phone",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <CanView access={[ACCESS.ACCOUNT_LOAN.VIEW_LIST]}>
              <li>
                <Button
                  color="light"
                  size="sm"
                  onClick={() => handleViewLoansClick(row.original)}
                  id={`view-loans-account-vendor-${row.original.id}`}
                >
                  <i className="mdi mdi-cash-multiple" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`view-loans-account-vendor-${row.original.id}`}
                >
                  View Loans
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_VENDOR.EDIT]}>
              <li>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEditClick(row.original)}
                  id={`edit-account-vendor-${row.original.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`edit-account-vendor-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_VENDOR.DELETE]}>
              <li>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(row.original)}
                  id={`delete-account-vendor-${row.original.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip
                  placement="top"
                  target={`delete-account-vendor-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Vendor" breadcrumbItem="Account Vendor List">
      <AccountVendorHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <AccountVendorAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />

      <ThemeModal
        isOpen={loansModal}
        toggle={toggleLoansModal}
        title={`Loans — ${selectedVendorForLoans?.name || "Vendor"}`}
        size="lg"
      >
        {selectedVendorForLoans?.id ? (
          <LoanSummaryCard borrowerType="vendor" borrowerId={selectedVendorForLoans.id} />
        ) : null}
      </ThemeModal>
    </PageWrapper>
  )
}

export default AccountVendorList
